// src/services/userBlocks.js
// Faz 1E Görev 2 — Kullanıcı engelleme (block) servisi.
//
// Mimari:
//   users/{blockerUid}/blocked/{blockedUid}
//     doc: { blockerId, blockedId, createdAt }
//
// Neden subcollection (discoveryBlocks root koleksiyonu yerine):
//   - Sahiplik path'ten garantili (rules için daha sıkı gate).
//   - Başkasının engelli listesini enumerate etmek path-read ile mümkün değil.
//   - follows/following ile eşleşir — sosyal graph katmanı tutarlı.
//
// Kullanım:
//   - blockUser(targetUid) / unblockUser(targetUid) — FAZ 0'dan itibaren
//     blockUserV3/unblockUserV3 callable üzerinden. blockUserV3 tek
//     transaction'da block doc'u yazar + takip ilişkisini İKİ YÖNDE koparır
//     + bekleyen takip isteklerini siler (Instagram paritesi). Eski direkt
//     write yolu edge temizliği yapmıyordu — engellenen kişi follower
//     edge'i üzerinden gizli içeriği okumaya devam edebiliyordu.
//   - getBlockedUids(viewerId, { force }) — feed/comments filtrelemesi için cache'li.
//   - watchIsBlocked(targetUid, cb) — profil kartı "Engelli" badge'ı için.
//   - isBlocked(targetUid) — one-shot kontrol (UI gating).
//
// Güvenlik: firestore.rules ikinci katman — self-block engelli, başkasının
// doc'una yazma engelli. Bu servis client-side best-effort doğrular.

import { auth, db } from "../firebase";
import { durumCoz } from "./iliskiDurumu";
import type { IliskiDurumu } from "./iliskiDurumu";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  limit as limitFn,
  onSnapshot,
  query,
} from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import {
  BLOCK_SUBCOLLECTION,
  BLOCK_LIST_PAGE_SIZE,
  BLOCK_CACHE_TTL_MS,
} from "../constants/userBlocks";

const REGION = "europe-west3";

/**
 * Engel dinleyicisi. İKİNCİ argüman EMİR #97/A5 ile eklendi: hata dalı artık
 * "hayır" demiyor, `bilinmiyor` diyor. Yalnız boolean okuyan eski çağıranlar
 * (`watchIsBlocked(uid, setIsBlockedUser)`) etkilenmez — az parametreli
 * fonksiyon çok parametreli imzaya atanabilir.
 */
export type EngelDinleyicisi = (engelli: boolean, durum: IliskiDurumu) => void;

function requireUser() {
  const u = auth.currentUser;
  if (!u) throw new Error("AUTH_REQUIRED");
  return u;
}

/** users/{blockerUid}/blocked/{blockedUid} */
function blockedRef(blockerUid: string, blockedUid: string) {
  return doc(
    db,
    "users",
    String(blockerUid),
    BLOCK_SUBCOLLECTION,
    String(blockedUid)
  );
}

/**
 * Hedef kullanıcıyı engelle. Idempotent — tekrar çağrı overwrite eder
 * (yeni `createdAt` ile).
 * FAZ 0: blockUserV3 callable — block doc + çift yönlü follow/istek
 * temizliği tek transaction'da.
 */
export async function blockUser(targetUid: string): Promise<true> {
  const me = requireUser();
  if (!targetUid) throw new Error("TARGET_REQUIRED");
  if (me.uid === targetUid) throw new Error("SELF_BLOCK_FORBIDDEN");

  const callable = httpsCallable(getFunctions(undefined, REGION), "blockUserV3");
  await callable({ targetUid: String(targetUid) });

  invalidateBlockedUidsCache(me.uid);
  return true;
}

/**
 * Engeli kaldır. Takip ilişkisi geri gelmez (yeniden takip gerekir).
 */
export async function unblockUser(targetUid: string): Promise<true> {
  const me = requireUser();
  if (!targetUid) throw new Error("TARGET_REQUIRED");

  const callable = httpsCallable(getFunctions(undefined, REGION), "unblockUserV3");
  await callable({ targetUid: String(targetUid) });

  invalidateBlockedUidsCache(me.uid);
  return true;
}

/**
 * Hedef kullanıcı engellendi mi? One-shot.
 */
export async function isBlocked(targetUid: string): Promise<boolean> {
  const me = auth.currentUser;
  if (!me || !targetUid || me.uid === targetUid) return false;
  try {
    const snap = await getDoc(blockedRef(me.uid, targetUid));
    return snap.exists();
  } catch {
    return false;
  }
}

/**
 * FAZ 2C — Hedef kullanıcı BENİ engellemiş mi? One-shot
 * (watchAmIBlockedBy'ın tek atımlık karşılığı — blockGuard çift yönlü
 * kontrolü için). Rules: users/{target}/blocked/{me} get'i blocked
 * viewer'a izinli.
 */
export async function isBlockedBy(targetUid: string): Promise<boolean> {
  const me = auth.currentUser;
  if (!me || !targetUid || me.uid === targetUid) return false;
  try {
    const snap = await getDoc(blockedRef(targetUid, me.uid));
    return snap.exists();
  } catch {
    return false;
  }
}

/**
 * Engel durumunu canlı izle.
 *
 * @returns unsubscribe
 */
export function watchIsBlocked(
  targetUid: string,
  cb: EngelDinleyicisi,
): () => void {
  const me = auth.currentUser;
  if (!me || !targetUid || me.uid === targetUid) {
    // Muhafız dalı GERÇEK cevap ("kendini engelleyemezsin") → `hayir`.
    cb(false, "hayir");
    return () => {};
  }
  // EMİR #97 / A5 — hata dalı artık "hayır" DEMİYOR. İkinci argüman üç
  // değerli durum (`iliskiDurumu.ts`); yalnız boolean okuyan eski çağıranlar
  // ETKİLENMEZ (watchIsMuted'ın muteType deseniyle aynı).
  const unsub = onSnapshot(
    blockedRef(me.uid, targetUid),
    (snap) => cb(snap.exists(), durumCoz(snap.exists())),
    () => cb(false, durumCoz(false, true)),
  );
  return unsub;
}

/**
 * P0-2 — Hedef kullanıcı BENİ bloklamış mı? Tek-doc get listener.
 *
 * Path: users/{targetUid}/blocked/{me.uid}
 *   rules: allow get if (isOwner(target) || blockedUid == auth.uid)
 *   list enumeration kapalı → saldırgan random target'ları tarayamaz.
 *
 * Profil sayfası bu listener true dönerse "Bu kullanıcıya erişilemiyor"
 * mask ekranı gösterir (Instagram pariteli). UI-level defansive layer;
 * gerçek içerik koruması Firestore rules tarafındadır (blocker'ın
 * post/clip/story'leri authorIsPrivate + canReadPrivateContent ile zaten
 * filtrelenir; ancak header/highlights/pin için maskeleme bu listener
 * üzerinden yapılır).
 *
 * @returns unsubscribe
 */
export function watchAmIBlockedBy(
  targetUid: string,
  cb: EngelDinleyicisi,
): () => void {
  const me = auth.currentUser;
  if (!me || !targetUid || me.uid === targetUid) {
    // Muhafız dalı GERÇEK cevap (kendin seni engelleyemezsin) → `hayir`.
    cb(false, "hayir");
    return () => {};
  }
  // Hedefin "blocked" subcollection'ında BENİM uid'imle anahtarlı doc.
  //
  // EMİR #97 / A5 — BU İZLEYİCİYİ İLK TURDA KAÇIRMIŞTIM; senkron testi
  // yakaladı ("hiçbir serviste çıplak `() => cb(false)` kalmadı" kuralı).
  // Beş servis sayıp altıncıyı atlamak, tam da bu emrin kapattığı
  // "yarım kapanan delik" sınıfı olurdu.
  //
  // ⚠️ BURADAKİ ASİMETRİ TERS YÖNDE, DİKKAT: bu yüzeyde `bilinmiyor`u
  // "engellenmişsin" gibi göstermek AŞIRI-GATE olur (profil sebepsiz
  // maskelenir). Boolean `false` bu yüzden korunuyor — maskeleme AÇILMAZ.
  // Durum yine de bildiriliyor ki çağıran taraf "engellenmemiş" diye
  // KESİN bir metin yazmasın; bilmezliği sessizce olumluya çevirmesin.
  const unsub = onSnapshot(
    blockedRef(targetUid, me.uid),
    (snap) => cb(snap.exists(), durumCoz(snap.exists())),
    () => cb(false, durumCoz(false, true)),
  );
  return unsub;
}

// ── Cache (follows.js ile aynı TTL deseni) ───────────────────────────────
let _cacheUid: string | null = null;
let _cacheUids: string[] = [];
let _cacheExpiresAt = 0;

/**
 * Aktif kullanıcının engellediği uid'leri döndürür. 60 sn cache'li.
 * Görev 3 (block-aware feed + comments) bu listeyi client-side filtre
 * için tüketecek.
 */
export async function getBlockedUids(
  viewerId: string | null | undefined,
  opts: { force?: boolean } = {},
): Promise<string[]> {
  if (!viewerId) return [];
  const { force = false } = opts;
  const now = Date.now();

  // 🐞 BUGGIFY · i-engel-onbellegi-bayat — SÜRESİ DOLMUŞ ÖNBELLEĞİ SERVİS ET.
  // Taklit edilen GERÇEK arıza: aynı TTL/saat-kayması sınıfı, ama BURADA
  // sonucu güvenliktir — yeni engellenen biri 60 sn (ya da saat kayarsa daha
  // uzun) istemci filtresinden GEÇER. SINANAN ONARIM İKİ KATMANLI:
  // `invalidateBlockedUidsCache` (engelleme sonrası düşürme) + sunucu tarafı
  // (`firestore.rules`/süzgeçler) — istemci önbelleği TEK hat olmamalı.
  // ⚙️ Üretimde derleme zamanı elenir (`false && …` → Terser bloğu siler).
  if (!force && (process.env.REACT_APP_BUGGIFY === "1" && window.__vwBuggify?.("i-engel-onbellegi-bayat") || (_cacheUid === viewerId && _cacheExpiresAt > now))
      && _cacheUid === viewerId) {
    return _cacheUids;
  }

  const set = new Set<string>();
  try {
    // 🐞 BUGGIFY · i-engel-listesi-okunamaz — ENGEL LİSTESİ ÇEKİLEMESİN.
    // Taklit edilen GERÇEK arıza: engel alt-koleksiyonu okuması kesinti/kota/
    // rules yüzünden düşer ve bu dosya hatayı YUTUP boş liste döndürür
    // (`catch` yalnız `console.warn` yazar, sonra boş listeyi 60 sn CACHE'LER).
    // SINANAN ONARIM İSTEMCİ DEĞİL SUNUCUDUR: engellenen kullanıcının içeriği
    // `firestore.rules` + sunucu süzgeçleriyle de gizlenmeli — istemci filtresi
    // TEK savunma hattı OLMAMALI. Bu, bir güvenlik değişmezinin İKİNCİ hattını
    // ölçen enjeksiyondur. ⚙️ Üretimde derleme zamanı elenir.
    if (process.env.REACT_APP_BUGGIFY === "1" && window.__vwBuggify?.("i-engel-listesi-okunamaz")) {
      throw new Error("vw-buggify: blocked list read failed");
    }
    const base = collection(db, `users/${viewerId}/${BLOCK_SUBCOLLECTION}`);
    const snap = await getDocs(query(base, limitFn(BLOCK_LIST_PAGE_SIZE)));
    snap.forEach((d) => {
      set.add(String(d.id));
    });
  } catch (e) {
    // eslint-disable-next-line no-console
    const hata = e as { code?: string; message?: string } | null | undefined;
    console.warn("[userBlocks] getBlockedUids okunamadı:", hata?.code || hata?.message);
  }

  const arr = Array.from(set);
  _cacheUid = viewerId;
  _cacheUids = arr;
  _cacheExpiresAt = Date.now() + BLOCK_CACHE_TTL_MS;
  return arr;
}

/**
 * block/unblock sonrası çağrılır ki getBlockedUids bir sonraki çağrıda
 * tazelensin. Dış modüller (Görev 3) block toggle sonrası kendi state'ini
 * invalidate etmek isterse de çağırabilir.
 *
 * @param viewerId belirtilmezse tüm cache düşürülür
 */
export function invalidateBlockedUidsCache(
  viewerId?: string | null,
): void {
  if (!viewerId || viewerId === _cacheUid) {
    _cacheUid = null;
    _cacheUids = [];
    _cacheExpiresAt = 0;
  }
}

/**
 * Hata kodlarını TR mesajına çevirir. UI'da toast göstermek için.
 */
export function mapError(e: unknown): string {
  const hata = e as { message?: string; code?: string } | null | undefined;
  const code = String(hata?.message || hata?.code || e || "ERR");
  if (code.includes("AUTH_REQUIRED")) return "Giriş yapmalısın.";
  if (code.includes("SELF_BLOCK_FORBIDDEN")) return "Kendini engelleyemezsin.";
  if (code.includes("TARGET_REQUIRED")) return "Hedef kullanıcı belirsiz.";
  if (code.includes("permission-denied")) return "İzin reddedildi.";
  return "İşlem başarısız. Lütfen tekrar dene.";
}

const api = {
  blockUser,
  unblockUser,
  isBlocked,
  isBlockedBy,
  watchIsBlocked,
  getBlockedUids,
  invalidateBlockedUidsCache,
  mapError,
};

export default api;
